import type { Practice, Segment, Settings, Step, Timeline } from '../types';
import { defaultMinutes } from './catalog';

export const MIN_POSE_SECONDS = 5;
export const MAX_POSE_SECONDS = 1800;
export const MAX_REPEAT = 20;

export const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Settings a practice starts with before the user changes anything. */
export function defaultSettings(p: Practice): Settings {
  return {
    rounds: p.rounds,
    secondsPerPose: null,
    minutes: defaultMinutes(p),
    bellInterval: 120,
    music: p.music ?? 'none',
    musicVolume: 0.5,
    bellVolume: 1,
    showNames: true,
    voice: false,
    overrides: {},
  };
}

/** Seconds and repeat count one pose step really uses after settings are applied. */
export function poseTiming(step: Extract<Step, { kind: 'pose' }>, stepIndex: number, s: Settings) {
  const o = s.overrides[stepIndex] ?? {};
  const seconds = clamp(o.duration ?? s.secondsPerPose ?? step.seconds, MIN_POSE_SECONDS, MAX_POSE_SECONDS);
  const repeat = clamp(o.repeat ?? step.repeat, 1, MAX_REPEAT);
  return { seconds, repeat };
}

/**
 * Builds the whole session as a list of segments. Total duration is always derived
 * from this list, so it can never disagree with what is played.
 */
export function buildTimeline(p: Practice, s: Settings): Timeline {
  const segments: Segment[] = [];
  let t = 0;

  const push = (step: Step, stepIndex: number, round: number, seconds: number, repeatIndex: number, repeatTotal: number) => {
    segments.push({
      index: segments.length,
      start: t,
      seconds,
      stepIndex,
      round,
      repeatIndex,
      repeatTotal,
      step,
      bellAtStart: step.kind === 'pose' || p.free === true,
    });
    t += seconds;
  };

  if (p.free) {
    const total = Math.max(MIN_POSE_SECONDS, (s.minutes ?? 20) * 60);
    const interval = clamp(s.bellInterval, MIN_POSE_SECONDS, MAX_POSE_SECONDS);
    const filler: Step = { kind: 'timer', title: 'Interval', seconds: interval };
    let left = total;
    let i = 0;
    while (left > 0.001) {
      const len = Math.min(interval, left);
      push(filler, 0, 1, len, i, 1);
      left -= len;
      i++;
    }
  } else {
    const rounds = p.roundsAdjustable ? clamp(s.rounds, 1, 99) : p.rounds;
    for (let round = 1; round <= rounds; round++) {
      p.steps.forEach((step, stepIndex) => {
        if (step.kind === 'pose') {
          const { seconds, repeat } = poseTiming(step, stepIndex, s);
          for (let r = 0; r < repeat; r++) push(step, stepIndex, round, seconds, r, repeat);
        } else if (step.kind === 'track') {
          push(step, stepIndex, round, step.seconds * step.loops, 0, 1);
        } else {
          const seconds = s.minutes ? s.minutes * 60 : step.seconds;
          push(step, stepIndex, round, seconds, 0, 1);
        }
      });
    }
  }

  const bells: number[] = segments.filter(seg => seg.bellAtStart).map(seg => seg.start);
  const last = segments[segments.length - 1];
  // Everything except a plain audio track ends with a bell.
  if (last && last.step.kind !== 'track') bells.push(t);

  return { segments, total: t, bells };
}

/** Index of the segment that contains `position`. */
export function segmentAt(tl: Timeline, position: number): number {
  const segs = tl.segments;
  if (position <= 0) return 0;
  for (let i = segs.length - 1; i >= 0; i--) {
    if (position >= segs[i].start) return i;
  }
  return 0;
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds - 0.0001));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, '0')}:${r.toString().padStart(2, '0')}`;
}

/** "2h 05m", "24 min", "45 sec" for cards and summaries. */
export function formatLength(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s} sec`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem.toString().padStart(2, '0')}m` : `${h}h`;
}

export const practiceSeconds = (p: Practice, s: Settings = defaultSettings(p)) => buildTimeline(p, s).total;
